import sha256 from 'crypto-js/sha256';
import { format } from 'date-fns-modern';
import { tz } from '@date-fns/tz';
import { Metadata } from 'next';
import { cache } from 'react';

import { getLocations, StrapiCountries, StrapiStopHour, Synonym } from '@/apis/strapi';
import { ScheduleProduct } from '@/apis/tds';
import { doTranslation } from '@/app/i18n';
import { LoginInfo } from '@/context/login-context';
import { Itinerary, ItineraryItem, Passenger, TicketCartGTM, TripDetailsForm } from '@/context/trip-search-context';
import { getCarrierInfo } from '@/utils/carriers';
import { localeSpecificPath } from '@/utils/localeUtils';
import { matchAlternateTexts } from '@/utils/search';
import { determineWhichFareToShow, getCarrier, getFares, getTransfers } from '@/utils/schedules';

export interface Country {
  label: string;
  value: string;
  alphabet: string;
  states: State[];
}

export interface State {
  label: string;
  value: string;
  alphabet: string;
  stops: Stop[]; // TODO:  These are cities, not stops.  Rename this
}

export interface Stop {
  label: string;
  value: string;
  alphabet: string;
}

export interface StopSearchObject {
  label: string;
  value: string;
  synonyms: Synonym[];
}

export const buildAlphabets = (alphabets: string[]): string[] => {
  return Array.from(new Set(alphabets)).sort((a: string, b: string) => a.localeCompare(b));
};

export const buildBaseURL = (country: string) => {
  switch (country) {
    case 'us':
      return process.env.URLS_US;
    case 'ca':
      return process.env.URLS_CA;
  }
  return process.env.URLS_US;
};

export const buildCount = (items: State[] | Stop[], alphabet: string): number => {
  return items.filter((i: any) => 'ALL' === alphabet || i.alphabet === alphabet).length;
};

export const buildCountries = (locations: StrapiCountries): Country[] => {
  return locations.data
    .filter((c: any) => c.attributes.states.data.length > 0)
    .map((c: any) => {
      return {
        label: c.attributes.country_name,
        value: c.attributes.country_id.toLowerCase(),
        alphabet: c.attributes.country_name[0],
        states: c.attributes.states.data
          .filter((s: any) => s.attributes.cities.data.length > 0)
          .map((s: any) => {
            const id = s.attributes.state_id;
            return {
              label: s.attributes.state_name,
              value: s.attributes.state_id.toLowerCase(),
              alphabet: s.attributes.state_name[0],
              stops: s.attributes.cities.data.map((s: any) => {
                return {
                  label: `${s.attributes.city_name}, ${id}`,
                  value: s.attributes.city_slug,
                  alphabet: s.attributes.city_name[0]
                };
              })
            };
          })
      };
    });
};

export const buildCountry = (countries: Country[], originCountrySlug: string): Country | undefined => {
  return countries.find((c: any) => c.value === originCountrySlug);
};

export const buildState = (country: Country, originStateSlug: string): State | undefined => {
  return country.states.find((s: any) => s.value === originStateSlug);
};

export const buildStop = (state: State, originStopSlug: string): Stop | undefined => {
  return state.stops.find((s: any) => s.value === originStopSlug);
};

export const buildStep = (
  originCountrySlug: string,
  originStateSlug: string,
  originStopSlug: string,
  destinationCountrySlug: string,
  destinationStateSlug: string,
  destinationStopSlug: string
): number => {
  if (
    originCountrySlug &&
    originStateSlug &&
    originStopSlug &&
    destinationCountrySlug &&
    destinationStateSlug &&
    destinationStopSlug
  ) {
    return 5;
  }
  if (originCountrySlug && originStateSlug && originStopSlug && destinationCountrySlug && destinationStateSlug) {
    return 4;
  }
  if (originCountrySlug && originStateSlug && originStopSlug && destinationCountrySlug) {
    return 3;
  }
  if (originCountrySlug && originStateSlug && originStopSlug) {
    return 3;
  }
  if (originCountrySlug && originStateSlug) {
    return 2;
  }
  if (originCountrySlug) {
    return 1;
  }
  return 1;
};

export const buildMetadata = async (
  prefix: string,
  lng: string,
  originCountrySlug: string,
  originStateSlug: string,
  originStopSlug: string,
  destinationCountrySlug: string,
  destinationStateSlug: string,
  destinationStopSlug: string
) => {
  const { t } = await doTranslation(lng);

  const locations = await getLocations();

  const step = buildStep(
    originCountrySlug,
    originStateSlug,
    originStopSlug,
    destinationCountrySlug,
    destinationStateSlug,
    destinationStopSlug
  );

  const siteName = process.env.NEXT_PUBLIC_COMPANY_PRETTY_NAME || 'Trailways';

  const buildObject = (description: string, title: string) => {
    const path = buildPath(
      prefix,
      lng,
      originCountrySlug,
      originStateSlug,
      originStopSlug,
      destinationCountrySlug,
      destinationStateSlug,
      destinationStopSlug,
      {}
    );
    const pathEN = buildPath(
      prefix,
      'en',
      originCountrySlug,
      originStateSlug,
      originStopSlug,
      destinationCountrySlug,
      destinationStateSlug,
      destinationStopSlug,
      {}
    );
    const pathES = buildPath(
      prefix,
      'es',
      originCountrySlug,
      originStateSlug,
      originStopSlug,
      destinationCountrySlug,
      destinationStateSlug,
      destinationStopSlug,
      {}
    );
    const pathFR = buildPath(
      prefix,
      'fr',
      originCountrySlug,
      originStateSlug,
      originStopSlug,
      destinationCountrySlug,
      destinationStateSlug,
      destinationStopSlug,
      {}
    );
    const url = `${buildBaseURL(process.env.COUNTRY ?? 'us')}${path}`;
    return {
      alternates: {
        languages: {
          'en': `${buildBaseURL('us')}${pathEN}`,
          'en-CA': `${buildBaseURL('ca')}${pathEN}`,
          'en-US': `${buildBaseURL('us')}${pathEN}`,
          'es': `${buildBaseURL('us')}${pathES}`,
          'fr': `${buildBaseURL('us')}${pathFR}`,
          'fr-CA': `${buildBaseURL('ca')}${pathFR}`,
          'x-default': `${buildBaseURL('us')}${pathEN}`
        },
        canonical: url
      },
      description: description,
      openGraph: {
        description: description,
        locale: `${lng}_${(process.env.COUNTRY ?? 'us').toUpperCase()}`,
        siteName: siteName,
        title: title,
        type: 'article',
        url: url,
        images: getSocialShareImages()
      },
      twitter: {
        card: 'summary_large_image',
        title: title,
        images: getSocialShareImages()
      },
      title: title
    };
  };

  switch (step) {
    case 5: {
      const countries = buildCountries(locations);
      const originCountry = buildCountry(countries, originCountrySlug);
      if (originCountry) {
        const originState = buildState(originCountry, originStateSlug);
        if (originState) {
          const originStop = buildStop(originState, originStopSlug);
          if (originStop) {
            const destinationCountry = buildCountry(countries, destinationCountrySlug);
            if (destinationCountry) {
              const destinationState = buildState(destinationCountry, destinationStateSlug);
              if (destinationState) {
                const destinationStop = buildStop(destinationState, destinationStopSlug);
                if (destinationStop) {
                  return buildObject(
                    t(
                      `{{ origin }} to {{ destination }} Bus Schedules and Fares ALL in one place. See the lowest price from Greyhound. See why millions of travelers use {{ site }}.`,
                      {
                        origin: originStop.label,
                        destination: destinationStop.label,
                        site: siteName
                      }
                    ),
                    t(`Bus from ${originStop.label} to ${destinationStop.label} &#8211; {{ site }}`, {
                      site: siteName
                    })
                  );
                }
              }
            }
          }
        }
      }
    }
    case 4: {
      const countries = buildCountries(locations);
      const originCountry = buildCountry(countries, originCountrySlug);
      if (originCountry) {
        const originState = buildState(originCountry, originStateSlug);
        if (originState) {
          const destinationCountry = buildCountry(countries, destinationCountrySlug);
          if (destinationCountry) {
            const destinationState = buildState(destinationCountry, destinationStateSlug);
            if (destinationState) {
              const state = destinationState.label;
              return buildObject(
                t(
                  `Search for Bus Tickets from {{ count }} ${
                    destinationState.stops.length === 1 ? 'city' : 'cities'
                  } in {{ state }} to hundreds of destinations across US & Canada on {{ site }}`,
                  {
                    count: destinationState.stops.length,
                    site: siteName,
                    state
                  }
                ),
                t('Find Bus Schedules from {{ state }} to cities across North America | {{ site }}', {
                  site: siteName,
                  state
                })
              );
            }
          }
        }
      }
    }
    case 3: {
      const countries = buildCountries(locations);
      const originCountry = buildCountry(countries, originCountrySlug);
      if (originCountry) {
        const originState = buildState(originCountry, originStateSlug);
        if (originState) {
          const originStop = buildStop(originState, originStopSlug);
          if (originStop) {
            return buildObject(
              t(
                `Explore bus schedules from {{ origin }}. Find the best ticket prices, and book bus tickets online with {{ site }}.`,
                {
                  origin: originStop.label,
                  site: siteName
                }
              ),
              t(`{{ site }} - Bus Schedules from {{ origin }}`, {
                site: siteName,
                origin: originStop.label
              })
            );
          }
        }
      }
    }
    case 2: {
      const countries = buildCountries(locations);
      const originCountry = buildCountry(countries, originCountrySlug);
      if (originCountry) {
        const originState = buildState(originCountry, originStateSlug);
        if (originState) {
          const state = originState.label;
          return buildObject(
            t(
              `Search for Bus Tickets from {{ count }} ${
                originState.stops.length === 1 ? 'city' : 'cities'
              } in {{ state }} to hundreds of destinations across US & Canada on {{ site }}`,
              {
                count: originState.stops.length,
                site: siteName,
                state
              }
            ),
            t('Find Bus Schedules from {{ state }} to cities across North America | {{ site }}', {
              site: siteName,
              state
            })
          );
        }
      }
    }
    case 1:
    default:
      return buildObject(
        t(
          'North American Bus schedules from Trailways. Find bus timetables and affordable bus tickets to and from New York, Las Vegas, Los Angeles, Chicago, Toronto and more.'
        ),
        t('Trailways - Bus Schedules')
      );
  }
};

export const buildPath = (
  prefix: string,
  lng: string,
  originCountrySlug: string,
  originStateSlug: string,
  originStopSlug: string,
  destinationCountrySlug: string,
  destinationStateSlug: string,
  destinationStopSlug: string,
  parameters: Record<string, string>
) => {
  const suffix = `${new URLSearchParams(parameters).toString()}`;
  let queryString = '';
  if (suffix) {
    queryString = '?' + suffix;
  }
  let path = prefix;
  let trailingSlash = '/';
  if (
    originCountrySlug &&
    originStateSlug &&
    originStopSlug &&
    destinationCountrySlug &&
    destinationStateSlug &&
    destinationStopSlug
  ) {
    path = `${prefix}/${originCountrySlug}-${originStateSlug}/${originStopSlug}/${destinationCountrySlug}-${destinationStateSlug}/${destinationStopSlug}${trailingSlash}${queryString}`;
  } else if (originCountrySlug && originStateSlug && originStopSlug && destinationCountrySlug && destinationStateSlug) {
    path = `${prefix}/${originCountrySlug}-${originStateSlug}/${originStopSlug}/${destinationCountrySlug}-${destinationStateSlug}${trailingSlash}${queryString}`;
  } else if (originCountrySlug && originStateSlug && originStopSlug && destinationCountrySlug) {
    path = `${prefix}/${originCountrySlug}-${originStateSlug}/${originStopSlug}/${destinationCountrySlug}${trailingSlash}${queryString}`;
  } else if (originCountrySlug && originStateSlug && originStopSlug) {
    path = `${prefix}/${originCountrySlug}-${originStateSlug}/${originStopSlug}${trailingSlash}${queryString}`;
  } else if (originCountrySlug && originStateSlug) {
    path = `${prefix}/${originCountrySlug}-${originStateSlug}${trailingSlash}${queryString}`;
  } else if (originCountrySlug) {
    path = `${prefix}/${originCountrySlug}${trailingSlash}${queryString}`;
  }
  //console.log("Turning " + path + " into " + localeSpecificPath );
  return localeSpecificPath(path, lng);
};

export const buildSearchOptions = (locations: any[], query: string) => {
  const text = query.split('.').join('').replace(/\s+/g, ' ').trimEnd().toLowerCase();
  if (!locations || !text) {
    return;
  }
  const alternateText = matchAlternateTexts(text);
  const words = text
    .split(' ')
    .filter((word) => word.length >= 2)
    .map((word) => word);
  const alternateWords = alternateText
    ? alternateText
        .split(' ')
        .filter((word) => word.length >= 2)
        .map((word) => word)
    : [];
  const data = locations
    .filter((c: any) => {
      const cityName = c.label.toLowerCase();
      if (cityName.includes(text)) {
        return true;
      }
      if (alternateText) {
        if (cityName.includes(alternateText)) {
          return true;
        }
      }
      for (const word of words) {
        if (cityName.includes(word)) {
          return true;
        }
      }
      for (const alternateWord of alternateWords) {
        if (cityName.includes(alternateWord)) {
          return true;
        }
      }
      if (
        c.synonyms.length &&
        c.synonyms.some((synonym: Synonym) => synonym.city_name_synonym.toLowerCase().includes(text))
      ) {
        return true;
      }
      return false;
    })
    .map((c: any) => {
      let rank = 0;
      const cityName = `${c.label.split(',')[0].toLowerCase() ?? ''}, ${c.label.split(',')[1].trim()}`;
      const cityNameMatchPos = cityName.indexOf(text);
      if (text === cityName) {
        rank = 3;
      } else if (cityNameMatchPos == 0) {
        rank = 2;
      } else if (alternateText) {
        const cityNameMatchPos = cityName.indexOf(alternateText);
        if (cityNameMatchPos == 0) {
          rank = 2;
        } else if (cityNameMatchPos > 0) {
          rank = 1;
        }
      } else if (cityNameMatchPos > 0 || c.label.toLowerCase().includes(text)) {
        rank = 1;
      }
      return {
        ...c,
        rank
      };
    });
  const sortOverride1 = 'New York (P.A.B.T.)';
  const sortOverride2 = 'New York (GW Bridge)';
  data.sort((a: any, b: any) => {
    if (a.rank === b.rank) {
      let sortReturn = a.value.localeCompare(b.value);
      // Override NY sort by reversing the sort for this case
      if (
        (a.value === sortOverride1 || a.value === sortOverride2) &&
        (b.value === sortOverride1 || b.value === sortOverride2)
      ) {
        sortReturn *= -1;
      }
      return sortReturn;
    }
    return b.rank - a.rank;
  });
  return data;
};

export const buildSearchMetadata = (lng: string, path: string, title: string, description: string | null = null) => {
  const metadata: Metadata = {
    metadataBase: process.env.TRAILWAYS_SITE_BASE ? new URL(process.env.TRAILWAYS_SITE_BASE) : undefined,
    title: title,
    description: description,
    openGraph: {
      type: 'article',
      siteName: 'Trailways',
      locale: `${lng}-${process.env.COUNTRY}`,
      modifiedTime: new Date().toISOString(),
      title: title,
      description: description || undefined,
      url: localeSpecificPath(path, lng),
      images: getSocialShareImages()
    },
    twitter: {
      card: 'summary_large_image',
      title: title,
      images: getSocialShareImages()
    },
    alternates: {
      languages: {
        'en': process.env.URLS_US + localeSpecificPath(path, 'en', 'US'),
        'en-CA': process.env.URLS_CA + localeSpecificPath(path, 'en', 'CA'),
        'en-US': process.env.URLS_US + localeSpecificPath(path, 'en', 'US'),
        'es': process.env.URLS_US + localeSpecificPath(path, 'es', 'US'),
        'fr': process.env.URLS_US + localeSpecificPath(path, 'fr', 'US'),
        'fr-CA': process.env.URLS_CA + localeSpecificPath(path, 'fr', 'CA'),
        'x-default': process.env.URLS_US + localeSpecificPath(path, 'en', 'US')
      },
      canonical: buildBaseURL(process.env.COUNTRY ?? 'us') + localeSpecificPath(path, lng)
    },
    robots:
      process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
        ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        : 'noindex, nofollow'
  };
  return metadata;
};

export const buildStops = (locations: StrapiCountries): StopSearchObject[] => {
  return locations?.data.flatMap((country) => {
    if (!country.attributes.states) {
      return [];
    }
    return country.attributes.states.data.flatMap((state) => {
      if (!state.attributes.cities) {
        return [];
      }
      return state.attributes.cities.data.map((city) => {
        return {
          label: `${city.attributes.city_name}, ${state.attributes.state_id}`,
          value: city.attributes.city_slug || '',
          synonyms: city.attributes?.synonym || []
        };
      });
    });
  });
};

const buildStrapiStopTime = (timeString: string | undefined) => {
  if (!timeString) {
    return '';
  }
  const [hourString, minutesString]: string[] = timeString.split(':');
  const hour: number = parseInt(hourString) % 24;
  return `${hour % 12 || 12}:${minutesString} ${hour < 12 ? 'am' : 'pm'}`;
};

const buildStrapiStopHoursValue = (hour: StrapiStopHour) => {
  hour.day_interval = hour.day_interval || '';
  const open_time = buildStrapiStopTime(hour.open_time);
  const close_time = buildStrapiStopTime(hour.close_time);

  // If we have an open and close time display it.
  // If we have neither, display closed
  // If we have one, but not the other, that's likely a mistake, so just display blank
  let time_display = '';
  if (open_time && close_time) {
    time_display = `${open_time} - ${close_time}`;
  } else if (!open_time && !close_time) {
    time_display = 'Closed';
  }
  return [
    hour.day_interval,
    {
      time: time_display,
      note: hour.note ?? ''
    }
  ];
};

export const buildStrapiStopHours = (hours: StrapiStopHour[] | undefined) => {
  let strapiStopHours: any[] = [];
  const strapiStopHoursProcessed: {
    [key: string]: any[];
  } = {};

  if (hours && hours.length) {
    strapiStopHours = hours
      .map((hour: StrapiStopHour) => {
        return buildStrapiStopHoursValue(hour);
      })
      .filter((value: any) => value);

    strapiStopHours.forEach((hours: any[]) => {
      if (!strapiStopHoursProcessed[hours[0]]) {
        strapiStopHoursProcessed[hours[0]] = [];
      }
      strapiStopHoursProcessed[hours[0]].push(hours[1]);
    });
  }

  return strapiStopHoursProcessed;
};

const getSocialShareImages = () => {
  // Hardcoded URLs because it's the same in every environment and country
  return [
    {
      url: 'https://cmscdn.trailways.com/site/og/trailways_wide.jpg',
      width: 1200,
      height: 630,
      alt: 'Trailways Logo',
      type: 'image/jpeg'
    }
  ];
};

export const getTicketCart = (
  itinerary: Itinerary | null,
  loginInfo: LoginInfo | null,
  newItinerary: ItineraryItem,
  passengers: Passenger[],
  tripDetailsForm: TripDetailsForm
) => {
  const trailwaysTimeZone = tz('America/New_York');
  const now = new Date();
  const ticketCart: TicketCartGTM = {
    affinity_id: loginInfo?.email || '',
    affinity_id_sha256: loginInfo?.email ? sha256(loginInfo.email).toString() : '',
    arrive_city: newItinerary.scheduleRun.destination.city,
    arrive_state: newItinerary.scheduleRun.destination.state,
    arrive_zip_code: newItinerary.scheduleRun.destination.iataCode || '',
    bags: 0,
    cart_date: format(now, 'yyyy-MM-dd', { in: trailwaysTimeZone }),
    cart_time: format(now, 'HH:mm:ss', { in: trailwaysTimeZone }),
    currency: `${process.env.NEXT_PUBLIC_REACT_APP_CURRENCY}`,
    delivery_email: loginInfo?.email || '',
    delivery_email_sha256: loginInfo?.email ? sha256(loginInfo?.email).toString() : '',
    depart_city: newItinerary.scheduleRun.origin.city,
    depart_state: newItinerary.scheduleRun.origin.state,
    depart_zip_code: newItinerary.scheduleRun.origin.iataCode || '',
    number_pax: passengers.length,
    promo_code: null,
    ticket_detail: 'OB',
    ticket_type: tripDetailsForm.isRoundTrip ? 'RT' : 'OW',
    web_transaction_id: tripDetailsForm.webTransactionId
  };
  const originTimeZone = tz(newItinerary.scheduleRun.origin.timeZone || 'America/New_York');
  const destinationTimeZone = tz(newItinerary.scheduleRun.destination.timeZone || 'America/New_York');
  if (!newItinerary.isReturnTrip) {
    const departTime = new Date(newItinerary.scheduleRun.departTime);
    const arriveTime = new Date(newItinerary.scheduleRun.arriveTime);
    const outbound = {
      depart_date: format(departTime, 'yyyy-MM-dd', { in: originTimeZone }),
      outbound_arrive_date: format(arriveTime, 'yyyy-MM-dd', { in: destinationTimeZone }),
      outbound_arrive_datetime_utc: `TWS_DateTime:${newItinerary.scheduleRun.arriveTime}`,
      outbound_arrive_time: format(arriveTime, 'hh:mm a', { in: destinationTimeZone }),
      outbound_carrier: newItinerary.scheduleRun.carrier.code.trim(),
      outbound_depart_datetime_utc: `TWS_DateTime:${newItinerary.scheduleRun.departTime}`,
      outbound_depart_time: format(departTime, 'hh:mm a', { in: originTimeZone }),
      outbound_vanity_fare: newItinerary.type
    };
    Object.assign(ticketCart, outbound);
  }
  if (newItinerary.isReturnTrip && tripDetailsForm.isRoundTrip) {
    const returningDepartTime = new Date(newItinerary.scheduleRun.departTime);
    const returningArriveTime = new Date(newItinerary.scheduleRun.arriveTime);
    const returningVanityFare = itinerary?.returning ? itinerary.returning.type : null;
    const returning = {
      return_arrive_date: format(returningArriveTime, 'yyyy-MM-dd', { in: originTimeZone }),
      return_arrive_datetime_utc: `TWS_DateTime:${newItinerary.scheduleRun.arriveTime}`,
      return_arrive_time: format(returningArriveTime, 'hh:mm a', { in: originTimeZone }),
      return_carrier: newItinerary.scheduleRun.carrier.code.trim(),
      return_date: format(returningDepartTime, 'yyyy-MM-dd', { in: destinationTimeZone }),
      return_depart_datetime_utc: `TWS_DateTime:${newItinerary.scheduleRun.departTime}`,
      return_depart_time: format(returningDepartTime, 'hh:mm a', { in: destinationTimeZone }),
      returning_vanity_fare: returningVanityFare
    };
    const outboundDepartTime = itinerary?.outbound?.scheduleRun.departTime
      ? new Date(itinerary?.outbound?.scheduleRun.departTime)
      : new Date();
    const outboundArriveTime = itinerary?.outbound?.scheduleRun.arriveTime
      ? new Date(itinerary?.outbound?.scheduleRun.arriveTime)
      : new Date();
    const outboundVanityFare = itinerary?.outbound ? itinerary?.outbound.type : null;
    const outbound = {
      depart_date: format(outboundDepartTime, 'yyyy-MM-dd', { in: originTimeZone }),
      outbound_arrive_date: format(outboundArriveTime, 'yyyy-MM-dd', { in: destinationTimeZone }),
      outbound_arrive_datetime_utc: `TWS_DateTime:${itinerary?.outbound?.scheduleRun.arriveTime}`,
      outbound_arrive_time: format(outboundArriveTime, 'hh:mm a', { in: destinationTimeZone }),
      outbound_carrier: itinerary?.outbound?.scheduleRun.carrier.code.trim(),
      outbound_depart_datetime_utc: `TWS_DateTime:${itinerary?.outbound?.scheduleRun.departTime}`,
      outbound_depart_time: format(outboundDepartTime, 'hh:mm a', { in: originTimeZone }),
      outbound_vanity_fare: outboundVanityFare
    };
    Object.assign(ticketCart, outbound);
    Object.assign(ticketCart, returning);
  }
  return ticketCart;
};

export const buildAndCacheScheduleMetaData = cache((schedules: ScheduleProduct[] | null) => {
  return buildScheduleMetaData(schedules);
});

export const buildScheduleMetaData = (schedules: ScheduleProduct[] | null) => {
  interface scheduleMetaData {
    averageBusTicketPrice: number;
    averageTravelTime: number | null;
    carrierCode: string;
    carrierName: string;
    directRoutes: number;
    earliestSchedule: ScheduleProduct | null;
    highestPrice: number | null;
    latestSchedule: ScheduleProduct | null;
    lowestPrice: number | null;
    maximumTravelTime: number;
    offerCount: number;
    shortestDistance: number | null;
    shortestSchedule: ScheduleProduct | null;
    shortestTravelTime: number | null;
    topCarriers: string[];
    tripsPerDay: number;
  }
  const scheduleMetaData: scheduleMetaData = {
    lowestPrice: null,
    highestPrice: null,
    shortestDistance: null,
    shortestSchedule: null,
    shortestTravelTime: null,
    averageTravelTime: null,
    earliestSchedule: null,
    latestSchedule: null,
    offerCount: 0,
    tripsPerDay: 0,
    carrierCode: '',
    carrierName: '',
    averageBusTicketPrice: 0,
    topCarriers: [],
    directRoutes: 0,
    maximumTravelTime: 0
  };

  if (schedules) {
    let travelDurations = 0;
    let saverFares = 0;
    let saverFareSchedules = 0;
    const carriers = [];
    scheduleMetaData.tripsPerDay = schedules.length;
    let travelDurationsList: any = [];
    for (const schedule of schedules) {
      if (
        scheduleMetaData.shortestDistance == null ||
        schedule.scheduleRun.mileage < scheduleMetaData.shortestDistance
      ) {
        scheduleMetaData.shortestDistance = schedule.scheduleRun.mileage;
        scheduleMetaData.shortestSchedule = {
          ...schedule,
          segments: []
        };
      }
      if (
        scheduleMetaData.earliestSchedule == null ||
        schedule.scheduleRun.departTime < scheduleMetaData.earliestSchedule.scheduleRun.departTime
      ) {
        scheduleMetaData.earliestSchedule = {
          ...schedule,
          segments: []
        };
      }
      if (
        scheduleMetaData.latestSchedule == null ||
        schedule.scheduleRun.departTime > scheduleMetaData.latestSchedule.scheduleRun.departTime
      ) {
        scheduleMetaData.latestSchedule = {
          ...schedule,
          segments: []
        };
      }
      const preferredFare = determineWhichFareToShow(schedule);
      if (preferredFare) {
        if (!scheduleMetaData.lowestPrice || preferredFare.amount < scheduleMetaData.lowestPrice) {
          scheduleMetaData.lowestPrice = preferredFare.amount;
          scheduleMetaData.carrierCode = schedule.scheduleRun.carrier.code;
          const thisCarrier = getCarrier(schedule);
          scheduleMetaData.carrierName = thisCarrier?.name || '';
        }
        if (!scheduleMetaData.highestPrice || preferredFare.amount > scheduleMetaData.highestPrice) {
          scheduleMetaData.highestPrice = preferredFare.amount;
        }
        scheduleMetaData.offerCount++;
      }
      if (
        !scheduleMetaData.shortestTravelTime ||
        schedule.scheduleRun.travelDuration < scheduleMetaData.shortestTravelTime
      ) {
        scheduleMetaData.shortestTravelTime = schedule.scheduleRun.travelDuration;
      }
      travelDurations += schedule.scheduleRun.travelDuration;
      travelDurationsList.push(schedule.scheduleRun.travelDuration);
      const fares = getFares(schedule);
      if (fares['saver'] && fares['saver'][0]) {
        saverFares += fares.saver[0]?.amount;
        saverFareSchedules += 1;
      }
      if (carriers.indexOf(schedule.scheduleRun.carrier.code) === -1) {
        carriers.push(schedule.scheduleRun.carrier.code);
      }
      const transfers = getTransfers(schedule.segments);
      if (transfers.length === 1) {
        scheduleMetaData.directRoutes += 1;
      }
    }
    if (travelDurations) {
      scheduleMetaData.averageTravelTime = travelDurations / schedules.length;
    }
    if (travelDurationsList.length) {
      scheduleMetaData.maximumTravelTime = Math.max(...travelDurationsList);
    }
    if (saverFares) {
      scheduleMetaData.averageBusTicketPrice = saverFares / saverFareSchedules;
    }
    if (carriers) {
      scheduleMetaData.topCarriers = carriers;
    }
  }
  scheduleMetaData.shortestDistance = Math.round(scheduleMetaData.shortestDistance || 0);
  return scheduleMetaData;
};
